import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Clock, DollarSign, CheckCircle, Tag, Search,
  ArrowRight, Target, Zap, Layers, Users, Bot,
  Gauge, Lock, AlertCircle, Coins
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FlowGrid, FlowBadge, FlowIndicator } from "../components/FlowVisuals";

const PATH_TYPE_META = {
  DIY: { label: "Self-Execute", Icon: Target, color: "#005CEE" },
  TOOL: { label: "System-Led", Icon: Bot, color: "#8A4FFF" },
  EXPERT: { label: "Expert-Led", Icon: Users, color: "#FF3366" },
  HYBRID: { label: "Hybrid", Icon: Layers, color: "#8A4FFF" },
};

function MiniStepFlow({ steps, color }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, width: "100%" }}>
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, minWidth: 48 }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: "var(--bg-soft)", border: `1.5px solid ${color}20`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.55rem", color }}>{i + 1}</span>
            </div>
            <span style={{
              fontFamily: "'Satoshi', sans-serif", fontSize: "0.5rem", fontWeight: 500,
              color: "var(--text-muted)", marginTop: 3, textAlign: "center",
              maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{step.action?.split(" ").slice(0, 2).join(" ") || step.step}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 1.5, minWidth: 8, background: `linear-gradient(90deg, ${color}20, ${color}08)`, borderRadius: 1 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ExecutionPathCard({ path, index, onSelect }) {
  const [hovering, setHovering] = useState(false);
  const meta = PATH_TYPE_META[path.path_type] || PATH_TYPE_META.TOOL;
  const steps = path.steps || [];
  const isRecommended = index === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.08 }}
      data-testid={`path-card-${index}`}
      onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}
      style={{
        background: "var(--bg-elevated)",
        border: isRecommended ? "1.5px solid rgba(0,92,238,0.15)" : "1px solid var(--glass-border)",
        borderRadius: 20, padding: 0, overflow: "hidden",
        boxShadow: hovering ? "0 12px 40px rgba(0,0,0,0.06)" : "var(--glass-shadow)",
        transition: "all 300ms ease",
        transform: hovering ? "translateY(-2px)" : "none",
        cursor: "pointer",
      }}
      onClick={() => onSelect && onSelect(path)}
    >
      {isRecommended && (
        <div style={{
          background: "var(--accent-subtle)", padding: "6px 20px",
          display: "flex", alignItems: "center", gap: 6,
          borderBottom: "1px solid rgba(0,92,238,0.08)",
        }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--primary)" }} />
          <span style={{ fontSize: "0.6rem", fontWeight: 700, color: "var(--accent-text)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em" }}>RECOMMENDED PATH</span>
        </div>
      )}
      <div style={{ padding: "1.3rem 1.6rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", background: `${meta.color}08`, borderRadius: 100, border: `1px solid ${meta.color}15` }}>
                <meta.Icon size={11} color={meta.color} strokeWidth={1.5} />
                <span style={{ fontSize: "0.62rem", fontWeight: 700, color: meta.color, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.04em" }}>{meta.label}</span>
              </div>
              <span style={{ fontSize: "0.58rem", fontWeight: 600, color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
                {["Plan A", "Plan B", "Plan C", "Plan D", "Plan E"][index] || `Path ${index + 1}`}
              </span>
            </div>
            <h3 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text-main)", lineHeight: 1.25, letterSpacing: "-0.02em", marginBottom: 4 }}>{path.title}</h3>
            <p style={{ fontSize: "0.78rem", color: "var(--text-secondary)", lineHeight: 1.5, fontFamily: "'Satoshi', sans-serif", maxWidth: 420 }}>{path.summary}</p>
          </div>
          <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
            {[
              { label: "Cost", value: `$${path.cost_min?.toLocaleString()}\u2013$${path.cost_max?.toLocaleString()}` },
              { label: "Time", value: `${path.time_min}\u2013${path.time_max}d` },
              { label: "Success", value: `${path.success_score}%`, color: meta.color },
            ].map(({ label, value, color: c }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "0.55rem", fontWeight: 600, color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
                <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.88rem", color: c || "var(--text-main)" }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1 }}><MiniStepFlow steps={steps} color={meta.color} /></div>
          <button data-testid={`select-path-btn-${index}`}
            style={{
              display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
              padding: "0.45rem 1.1rem", borderRadius: 100, border: "none",
              background: hovering ? "var(--primary)" : "var(--bg-soft)",
              color: hovering ? "#fff" : "var(--text-secondary)",
              fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "0.76rem",
              cursor: "pointer", transition: "all 300ms ease",
              boxShadow: hovering ? "0 4px 14px var(--primary-glow)" : "none",
            }}>
            <ArrowRight size={12} strokeWidth={1.5} /> Select path
          </button>
        </div>
      </div>
      {path.why_relevant && (
        <div style={{
          padding: "8px 1.6rem 12px", fontSize: "0.7rem", color: "var(--text-muted)",
          fontFamily: "'Satoshi', sans-serif", fontStyle: "italic",
          borderTop: "1px solid var(--glass-border)", background: "var(--bg-surface)",
        }}>
          {path.why_relevant}
        </div>
      )}
    </motion.div>
  );
}

export default function TaskResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(query);
  const [creatingTask, setCreatingTask] = useState(false);
  const [costModal, setCostModal] = useState(null);
  const { user } = useAuth();

  useEffect(() => { if (!query) return; setSearchInput(query); fetchResults(query); }, [query]);

  const fetchResults = async (q) => {
    setLoading(true); setError(null);
    try { const { data } = await api.post("/api/tasks/search", { query: q }); setResults(data); }
    catch (e) { setError("Could not fetch results. Please try again."); }
    finally { setLoading(false); }
  };

  const handleSearch = () => {
    if (searchInput.trim().length < 2) return;
    navigate(`/search?q=${encodeURIComponent(searchInput.trim())}`);
  };

  const handleSelectPath = async (path) => {
    if (!user) { navigate("/login"); return; }
    if (creatingTask) return;
    try {
      const category = parsed?.detected_category || "general";
      const [estimateRes, walletRes] = await Promise.all([
        api.post("/api/wallet/estimate-price", { category, complexity_score: parsed?.confidence || 0.5, urgency_score: 0.5, query_length: query.length }),
        api.get("/api/wallet/balance"),
      ]);
      setCostModal({ path, estimate: estimateRes.data, wallet: walletRes.data });
    } catch (e) {
      setCostModal({ path, estimate: { suggested_price: 25, doer_payout: 20, platform_fee: 5, min_price: 10, max_price: 50 }, wallet: { balance: 0 } });
    }
  };

  const confirmAndCreateTask = async () => {
    if (!costModal) return;
    const { path, estimate, wallet } = costModal;
    const cost = estimate.suggested_price;
    if (wallet.balance < cost) { setCostModal(null); navigate("/wallet"); return; }
    setCreatingTask(true); setCostModal(null);
    try {
      const { data } = await api.post("/api/workspace/tasks", {
        query, interpreted_goal: query, category: parsed?.detected_category || "general",
        tags: [parsed?.detected_intent, parsed?.detected_category, parsed?.detected_industry].filter(Boolean),
        confidence: parsed?.confidence || 0.7, credit_cost: cost,
        budget_min: parsed?.typical_budget_range?.min || path.cost_min || 500,
        budget_max: parsed?.typical_budget_range?.max || path.cost_max || 5000,
        timeframe_min: parsed?.typical_time_range?.min || path.time_min || 7,
        timeframe_max: parsed?.typical_time_range?.max || path.time_max || 30,
        selected_path: { title: path.title, type: path.path_type, summary: path.summary, steps: path.steps },
        execution_mode: path.path_type === "TOOL" ? "quick" : path.path_type === "EXPERT" ? "deep" : "quick",
      });
      navigate(`/workspace/${data.id}`);
    } catch (e) { alert(e.response?.data?.detail || "Failed to create task"); setCreatingTask(false); }
  };

  const parsed = results?.parsed_task;
  const paths = results?.recommended_paths || [];
  const businesses = results?.matching_businesses || [];
  const records = results?.matching_records || [];
  const related = results?.related_tasks || [];
  const confidence = Math.round((parsed?.confidence || 0.7) * 100);

  return (
    <div style={{ paddingTop: 84, minHeight: "100vh", position: "relative" }}>
      <FlowGrid opacity={0.02} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 64px", position: "relative", zIndex: 1 }}>
        {/* Search bar */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: "2rem" }}>
          <button onClick={() => navigate("/")} data-testid="back-btn" style={{
            width: 38, height: 38, borderRadius: "50%", border: "1px solid var(--bg-neutral)",
            background: "var(--bg-elevated)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", transition: "all 200ms",
          }}><ArrowLeft size={16} color="var(--text-muted)" /></button>
          <div style={{
            flex: 1, display: "flex", gap: 8, alignItems: "center",
            background: "var(--bg-elevated)", border: "1px solid var(--bg-neutral)",
            borderRadius: 14, padding: "10px 14px", transition: "all 300ms",
          }}>
            <Search size={16} color="var(--text-muted)" strokeWidth={1.5} />
            <input data-testid="result-search-input" value={searchInput}
              onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSearch()}
              style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontSize: "0.92rem", fontFamily: "'Satoshi', sans-serif", color: "var(--text-main)", fontWeight: 500 }} />
          </div>
          <button className="btn-primary" onClick={handleSearch} data-testid="result-search-btn" style={{ padding: "0.55rem 1.2rem", borderRadius: 100, flexShrink: 0 }}>Search</button>
        </div>

        {loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: "center", padding: "6rem 0" }}>
            <div style={{ position: "relative", width: 80, height: 80, margin: "0 auto 1.5rem" }}>
              <svg viewBox="0 0 80 80" style={{ width: 80, height: 80 }}>
                <rect x="10" y="10" width="60" height="60" rx="14" fill="none" stroke="url(#loadGrad)" strokeWidth="1.5"
                  strokeDasharray="200" style={{ animation: "traceLoad 1.5s ease-in-out infinite" }} />
                <defs><linearGradient id="loadGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#005CEE" /><stop offset="50%" stopColor="#8A4FFF" /><stop offset="100%" stopColor="#FF3366" /></linearGradient></defs>
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--primary)", animation: "flowPulse 1.5s ease-in-out infinite" }} />
              </div>
            </div>
            <p style={{ color: "var(--text-main)", fontFamily: "'Manrope', sans-serif", fontSize: "1rem", fontWeight: 600, marginBottom: 4 }}>Routing execution...</p>
            <p style={{ color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem" }}>Mapping semantic flows to execution paths</p>
            <style>{`
              @keyframes traceLoad { 0% { stroke-dashoffset: 200; opacity: 0.3; } 50% { stroke-dashoffset: 0; opacity: 1; } 100% { stroke-dashoffset: -200; opacity: 0.3; } }
              @keyframes flowPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.3; transform: scale(0.8); } }
            `}</style>
          </motion.div>
        )}

        {error && !loading && (
          <div style={{ background: "var(--pink-subtle)", border: "1px solid rgba(255,51,102,0.15)", borderRadius: 12, padding: "1rem 1.2rem", color: "var(--pink)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.88rem" }}>{error}</div>
        )}

        {!loading && results && parsed && (
          <>
            {/* Task Header */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="task-header"
              className="glass-card" style={{ padding: "1.8rem 2rem", marginBottom: "2rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--primary)" }} />
                <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "var(--accent-text)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em" }}>TASK UNDERSTOOD</span>
                <FlowBadge count={paths.length || Math.max(3, Math.min(9, query.length))} style={{ marginLeft: 6 }} />
              </div>
              <h1 data-testid="task-title" style={{
                fontFamily: "'Manrope', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                fontWeight: 700, letterSpacing: "-0.04em", color: "var(--text-main)", marginBottom: 12, lineHeight: 1.1,
              }}>{query}</h1>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {[
                  parsed.detected_intent !== "general" && { label: parsed.detected_intent, bg: "var(--bg-soft)", color: "var(--text-secondary)", border: "var(--bg-neutral)" },
                  parsed.detected_category !== "general" && { label: parsed.detected_category, bg: "var(--accent-subtle)", color: "var(--accent-text)", border: "rgba(0,92,238,0.12)" },
                  parsed.detected_industry && { label: parsed.detected_industry, bg: "var(--violet-subtle)", color: "var(--violet)", border: "rgba(138,79,255,0.12)" },
                ].filter(Boolean).map((tag, i) => (
                  <span key={i} style={{ background: tag.bg, color: tag.color, border: `1px solid ${tag.border}`, fontSize: "0.66rem", fontWeight: 600, padding: "0.2rem 0.6rem", borderRadius: 100, fontFamily: "'JetBrains Mono', monospace" }}>
                    {tag.label?.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: "var(--accent-subtle)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Gauge size={15} color="var(--primary)" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.55rem", fontWeight: 600, color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>Confidence</div>
                    <div data-testid="confidence-value" style={{ fontSize: "1rem", fontWeight: 700, color: "var(--accent-text)", fontFamily: "'Manrope', sans-serif" }}>{confidence}%</div>
                  </div>
                </div>
                {parsed.typical_budget_range && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, background: "var(--violet-subtle)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <DollarSign size={15} color="var(--violet)" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.55rem", fontWeight: 600, color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>Budget range</div>
                      <div data-testid="budget-value" style={{ fontSize: "0.92rem", fontWeight: 700, color: "var(--text-main)", fontFamily: "'Manrope', sans-serif" }}>${parsed.typical_budget_range.min?.toLocaleString()}–${parsed.typical_budget_range.max?.toLocaleString()}</div>
                    </div>
                  </div>
                )}
                {parsed.typical_time_range && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, background: "var(--pink-subtle)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Clock size={15} color="var(--pink)" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.55rem", fontWeight: 600, color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>Timeframe</div>
                      <div data-testid="timeframe-value" style={{ fontSize: "0.92rem", fontWeight: 700, color: "var(--text-main)", fontFamily: "'Manrope', sans-serif" }}>{parsed.typical_time_range.min}–{parsed.typical_time_range.max} days</div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Execution Paths */}
            {paths.length > 0 && (
              <div style={{ marginBottom: "2.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1.2rem" }}>
                  <div className="trust-bar" />
                  <h2 data-testid="paths-heading" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text-main)", letterSpacing: "-0.02em" }}>{paths.length} execution paths</h2>
                  <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>Select one to begin</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {paths.map((path, i) => <ExecutionPathCard key={i} path={path} index={i} onSelect={handleSelectPath} />)}
                </div>
              </div>
            )}

            {/* Related Tasks */}
            {related.length > 0 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.8rem" }}>
                  <div className="trust-bar" />
                  <h2 data-testid="related-heading" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text-main)" }}>Related tasks</h2>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {related.map((t, i) => (
                    <motion.button key={i} data-testid={`related-task-${i}`}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.04 }}
                      onClick={() => navigate(`/search?q=${encodeURIComponent(t.title)}`)}
                      style={{
                        padding: "0.35rem 0.85rem", borderRadius: 100,
                        background: "var(--bg-elevated)", border: "1px solid var(--bg-neutral)",
                        cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem",
                        fontWeight: 500, color: "var(--text-secondary)", transition: "all 200ms",
                        display: "flex", alignItems: "center", gap: 5,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(0,92,238,0.15)"; e.currentTarget.style.color = "var(--text-main)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--bg-neutral)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
                      <Tag size={10} strokeWidth={1.5} /> {t.title}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!loading && !results && !error && (
          <div style={{ textAlign: "center", padding: "8rem 0" }}>
            <Search size={36} color="var(--bg-neutral)" style={{ margin: "0 auto 1rem" }} />
            <p style={{ color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.88rem" }}>Enter a task above to see execution paths.</p>
          </div>
        )}
      </div>

      {/* Cost Confirmation Modal */}
      <AnimatePresence>
        {costModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.15)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
            onClick={() => setCostModal(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              style={{ background: "var(--bg-elevated)", borderRadius: 20, padding: "2rem", maxWidth: 420, width: "100%", border: "1px solid var(--glass-border)", boxShadow: "0 24px 80px rgba(0,0,0,0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--accent-subtle)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Coins size={16} color="var(--primary)" />
                </div>
                <div>
                  <h3 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.05rem" }}>Confirm Execution</h3>
                  <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>Credits will be secured for this task</p>
                </div>
              </div>
              <div style={{ background: "var(--bg-soft)", borderRadius: 14, padding: "1.2rem", marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-secondary)" }}>Task cost</span>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1.1rem" }}>{costModal.estimate.suggested_price} credits</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>Executor payout (80%)</span>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 500, fontSize: "0.82rem", color: "var(--primary)" }}>{costModal.estimate.doer_payout}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>Platform fee (20%)</span>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 500, fontSize: "0.82rem", color: "var(--text-secondary)" }}>{costModal.estimate.platform_fee}</span>
                </div>
                <div style={{ height: 1, background: "var(--bg-neutral)", margin: "10px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-secondary)" }}>Your balance</span>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.92rem", color: costModal.wallet.balance >= costModal.estimate.suggested_price ? "var(--primary)" : "var(--pink)" }}>
                    {costModal.wallet.balance} credits
                  </span>
                </div>
              </div>
              {costModal.wallet.balance < costModal.estimate.suggested_price && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", background: "var(--pink-subtle)", border: "1px solid rgba(255,51,102,0.15)", borderRadius: 10, padding: "0.7rem 0.9rem", marginBottom: 16 }}>
                  <AlertCircle size={14} color="var(--pink)" />
                  <div>
                    <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", fontWeight: 600, color: "var(--pink)" }}>Insufficient credits</p>
                    <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.68rem", color: "var(--text-muted)" }}>You need {costModal.estimate.suggested_price - costModal.wallet.balance} more credits</p>
                  </div>
                </div>
              )}
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)", lineHeight: 1.5, marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 6 }}>
                <Lock size={12} color="var(--primary)" style={{ flexShrink: 0, marginTop: 1 }} />
                Credits will be secured until the task is completed.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button data-testid="cost-modal-cancel" onClick={() => setCostModal(null)} className="btn-secondary" style={{ padding: "0.5rem 1.2rem" }}>Cancel</button>
                {costModal.wallet.balance >= costModal.estimate.suggested_price ? (
                  <button data-testid="cost-modal-confirm" onClick={confirmAndCreateTask} disabled={creatingTask} className="btn-primary"
                    style={{ padding: "0.5rem 1.4rem", display: "flex", alignItems: "center", gap: 6, opacity: creatingTask ? 0.5 : 1 }}>
                    <Lock size={12} /> {creatingTask ? "Securing..." : `Secure ${costModal.estimate.suggested_price} credits`}
                  </button>
                ) : (
                  <button data-testid="cost-modal-add-credits" onClick={() => { setCostModal(null); navigate("/wallet"); }} className="btn-primary"
                    style={{ padding: "0.5rem 1.4rem", display: "flex", alignItems: "center", gap: 6 }}>
                    Add Credits
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
